// flow.$projectid.tsx
import { Steps } from 'intro.js-react'
import { throttle } from 'lodash'
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useParams, useSearchParams } from 'react-router'
import { Toaster, toast } from 'sonner'
import '@/styles/introjs.css'
import {
  ReactFlowProvider,
  useReactFlow,
  useStore,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import '@/styles/flow.css'
import { backgroundPresets } from '@/utils/backgroundPresets'
import { useUser } from '@/context/UserContext'
import { useFileStore } from '@/stores/fileStore'
import { useEdgeHandler } from '@/hooks/useEdgeHandler'
import { useIntroGuide } from '@/hooks/useIntroGuide'
import { useInitializationTour } from '@/hooks/useInitializationTour'
import { useViewportSettings } from '@/hooks/useViewportSettings'
import { useMenuManagement } from '@/hooks/useMenuManagement'
import { useNodeOperations } from '@/hooks/useNodeOperations'
import { useNodeCreation } from '@/hooks/useNodeCreation'

import { createBlueprintContainer } from '@/utils/blueprintUtils'
import { useSyncedProjectStore } from '@/stores/syncedProjectStore'

import {
  useProjectPhaseStore,
} from '@/stores/projectPhaseStore'
import { useSidePanelState } from '@/hooks/useSidePanelState'
import '@/styles/animations.css'
import { useNodeCreationHandlers } from '@/hooks/nodeCreationHandlers'
import { useClipboardUtils } from '@/utils/clipboardUtils'
import { useKeyboardShortcuts } from '@/utils/keyboardShortcuts'
import { useAutoLayout } from '@/hooks/useAutoLayout'
import { useProjectInitialization } from '@/hooks/useProjectInitialization'
import { InitializationMessage } from '@/components/project/InitializationMessage'

// Add lodash debounce for search functionality  
import { debounce } from 'lodash'

// Import new modular hooks and components
import {
  useFlowState,
  useFlowFileHandlers,
  useFlowData,
  useFlowGlobalHandlers,
  useFlowProjectSettings,
} from '@/components/Flow/hooks'
import FlowOverlays from '@/components/Flow/components/FlowOverlays'
import FlowDialogs from '@/components/Flow/components/FlowDialogs'
import FlowContextMenus from '@/components/Flow/components/FlowContextMenus'
import FlowCanvas from '@/components/Flow/components/FlowCanvas'



function ErrorFallback({ error, resetErrorBoundary }) {
  return (
    <div role="alert">
      <p>Something wasn't chopped right:</p>
      <pre>{error.message}</pre>
      <button onClick={resetErrorBoundary}>Try again</button>
    </div>
  )
}

function Flow() {
  const { projectId } = useParams()
  const user = useUser()
  const [searchParams] = useSearchParams()
  const docName = searchParams.get('docName')
  const projectName = searchParams.get('projectName')
  const pendingBlueprint = useProjectPhaseStore((state) => state.pendingBlueprint)
  const setPendingBlueprint = useProjectPhaseStore((state) => state.setPendingBlueprint)
  const {
    setProjectId,
    fetchFiles,
    fetchFileGroups,
  } = useFileStore()

  const {
    isReady,
    useCursorStateSynced,
    useNodesStateSynced,
    useEdgesStateSynced, } = useSyncedProjectStore(docName || '')
  const {
    selectNode,
    state: sidePanelState,
  } = useSidePanelState()

  // Use new modular flow state hook
  const {
    state: flowState,
    handlers: flowHandlers,
    currentBackgroundPreset,
  } = useFlowState(projectName || undefined)
  // Extract state variables from flowState (only what we need)
  const {
    theme,
    backgroundPreset,
    showGlitch,
    showMap,
    showNodeIndex,
    showFileDialog,
    showBlueprints,
    showProjectSettings,
    projectInfo,
    panOnScroll,
  } = flowState

  // Extract handlers from flowHandlers (only what we need)
  const {
    toggleTheme,
    setBackgroundPreset,
    setShowMap,
    setShowNodeIndex,
    setShowFileDialog,
    setShowBlueprints,
    setShowProjectSettings,
    setFlowBounds,
    setPanOnScroll,
    setDebug,
    setShowGlitch,
  } = flowHandlers

  if (!isReady) return <div>Loading...</div>
  useEffect(() => {
    if (projectId) {
      setProjectId(projectId)
      fetchFiles(projectId)
      fetchFileGroups(projectId)
    }
  }, [projectId]) // Only depend on projectId to prevent infinite loops

  // Set browser tab title to project name
  useEffect(() => {
    const title = projectName || projectInfo?.projectName || 'Spechops Flow'
    document.title = title

    // Cleanup function to reset title when component unmounts
    return () => {
      document.title = 'Spechops'
    }
  }, [projectName, projectInfo?.projectName])

  const [cursors, onMouseMoveBase] = useCursorStateSynced()
  const onMouseMove = useCallback(
    throttle((event) => onMouseMoveBase(event), 1000, {
      leading: true,
      trailing: true,
    }),
    [onMouseMoveBase]
  )
  const [
    nodes,
    setNodes,
    setNodeSynced,
    onNodesChange,
    globalNodeEdgeUndoManager,
    startDrag,
    stopDrag,
    forceSyncNode,
  ] = useNodesStateSynced(user.id || '')
  const [edges, setEdges, onEdgesChange] = useEdgesStateSynced()

  // Project initialization system - pass current nodes
  const {
    startingAngle,
    showInitializationMessage,
    initializationMessage,
    initializationSteps,
    currentStep,
    dismissInitializationMessage,
    nextStep,
    previousStep,
    // refreshInitializationStatus, // Temporarily commented out
  } = useProjectInitialization(nodes)

  const validEdges = edges.filter(
    (edge) => edge &&
      typeof edge === 'object' &&
      'id' in edge &&
      'source' in edge &&
      'target' in edge &&
      typeof edge.source === 'string' &&
      typeof edge.target === 'string'
  )
  const ref = useRef<HTMLDivElement>(null)
  const { screenToFlowPosition, getNode, setCenter, updateNodeData } =
    useReactFlow()
  const zoomLevel = useStore((s) => s.transform[2])
  const { viewportSettings, setViewportSettings, initializeViewport } =
    useViewportSettings(projectId!)
  const {
    introEnabled,
    setIntroEnabled,
    introSteps,
    introStepsIndex,
    setIntroStepsIndex,
    introOptions,
  } = useIntroGuide()
  // Initialization tour state - use refs to prevent re-render issues
  const [showInitializationTour, setShowInitializationTour] = useState(false);
  const initializationTourNodeIdRef = useRef<string | undefined>();
  // Get initialization tour steps - use ref to prevent re-render issues
  const {
    introSteps: initTourSteps,
    introOptions: initTourOptions
  } = useInitializationTour(startingAngle || 'quickstart', initializationTourNodeIdRef.current);

  // Determine which steps and options to use
  const currentSteps = showInitializationTour ? initTourSteps : introSteps;
  const currentOptions = showInitializationTour ? initTourOptions : introOptions;
  const {
    nodeMenu,
    paneMenu,
    sectionMenu,
    onNodeContextMenu,
    onPaneContextMenu,
    onPaneClick,
    onMove,
    handleSectionSelect,
    setNodeMenu,
    setPaneMenu,
    setSectionMenu,
  } = useMenuManagement(ref, getNode)
  const {
    addNode,
    removeNode,
    duplicateNode,
    onNodeDragStart,
    onNodeDragStop,
    handleSectionStateChange,
    onNodeClick, findPresenceNode,
  } = useNodeOperations(nodes, setNodes, setNodeSynced, setEdges, getNode, user, startDrag, stopDrag, forceSyncNode, selectNode, onNodesChange)

  const {
    createQuickStartNode,
    // createnodequickstartdynamic, // Commented out unused
    createDocumentNode,
    createDocumentNodeWithContent,
    createNodeIntellica,
    createSmartNotesNode,
    createDocumentDesignerNode,
    createNodeDiagrams,    // createNodeDiagramsMermaid, // Commented out unused
    createFileMinerNode,
    createJavascriptNode,
    createImageGeneratorNode,
    createNodeResource,
    createNodeWebExtract,
    createNodeByType,
  } = useNodeCreation(addNode, user.email)
  // Handle pending blueprint addition
  useEffect(() => {
    if (pendingBlueprint) {
      try {
        const { containerNode, blueprintNodes } = createBlueprintContainer(
          pendingBlueprint,
          { x: 100, y: 100 }
        )

        // Add all nodes at once (container first, then children) for proper parent-child relationship
        const allNodesToAdd = [containerNode, ...blueprintNodes]

        // Add all nodes in one operation
        setNodes((currentNodes) => [...currentNodes, ...allNodesToAdd])

        // Clear the pending blueprint
        setPendingBlueprint(null)

        // Show success toast
        toast(`Blueprint "${pendingBlueprint.name}" added to project with ${blueprintNodes.length} nodes`, {
          description: 'The blueprint has been encapsulated in a group container',
          duration: 3000,
        })
      } catch (error) {
        console.error('Error creating blueprint container:', error)
        toast(`Failed to add blueprint "${pendingBlueprint.name}"`, {
          description: error instanceof Error ? error.message : 'Unknown error occurred',
          duration: 5000,
        })
        setPendingBlueprint(null) // Clear it even on error to prevent infinite retries
      }
    }
  }, [pendingBlueprint, addNode, setPendingBlueprint])  // Zoom to relevant node when onboarding is active and a node is created
  useEffect(() => {    // Only zoom if onboarding is active (project not initialized)
    if (showInitializationMessage && nodes.length > 0) {
      // Find relevant nodes based on starting angle
      let targetNode: any = null;

      if (startingAngle === 'quickstart') {
        targetNode = nodes.find(node => node.type === 'nodequickstart');
      } else if (startingAngle === 'quickstartdynamic') {
        targetNode = nodes.find(node => node.type === 'nodequickstartdynamic');
      } else if (startingAngle === 'document') {
        targetNode = nodes.find(node => node.type === 'nodedocument');
      } else if (startingAngle === 'smartnotes') {
        targetNode = nodes.find(node => node.type === 'nodesmartnotes');
      }
      // If we found a target node and it has a position, zoom to it
      if (targetNode && targetNode.position) {
        // Calculate node dimensions (estimate)
        const nodeWidth = 900; // Approximate node width
        const nodeHeight = 630; // Approximate node height

        // Center on the node with a slight offset
        setCenter(
          targetNode.position.x + nodeWidth / 2,
          targetNode.position.y + nodeHeight / 2,
          {
            zoom: 1.2, // Nice zoom level to see the node clearly
            duration: 800 // Smooth animation
          }
        );
      }
    }
  }, [nodes.length, showInitializationMessage, startingAngle, setCenter]);

  // TODO: Re-enable node change detection once basic initialization is working
  // Refresh initialization status when nodes change (debounced)
  // useEffect(() => {
  //   if (nodes.length > 0) {
  //     const timeoutId = setTimeout(() => {
  //       // Call refresh directly to avoid dependency issues
  //       if (refreshInitializationStatus) {
  //         refreshInitializationStatus();
  //       }
  //     }, 1000); // Increased debounce delay

  //     return () => clearTimeout(timeoutId);
  //   }
  // }, [nodes.length]); // Remove refreshInitializationStatus from dependencies

  const { onCreatingAnnotationNode, onCreatingRandomSequenceNode, newNode } =
    useNodeCreationHandlers(user)
  const { hasCopiedNode, copyNodeToClipboard, pasteNodeFromClipboard } =
    useClipboardUtils(getNode, screenToFlowPosition, addNode)
  // Search functionality state
  const [searchTerm, setSearchTerm] = useState('')
  const [searchMatches, setSearchMatches] = useState<string[]>([])
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0)
  const [isSearchVisible, setIsSearchVisible] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Search node content function
  const searchNodeContent = useCallback((node: any, term: string) => {
    if (!node.data || !term.trim()) return false

    const searchIn = [
      node.data.label || '',
      node.data.context || '',
      node.data.contentMarkdown || ''
    ].join(' ').toLowerCase()

    return searchIn.includes(term.toLowerCase())
  }, [])

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((term: string) => {
      if (term.length < 7) {
        setSearchMatches([])
        // Clear search match data from all nodes
        nodes.forEach(node => {
          if (node.data?.isSearchMatch || node.data?.isCurrentSearchMatch) {
            setNodeSynced({
              ...node,
              data: { ...node.data, isSearchMatch: false, isCurrentSearchMatch: false }
            })
          }
        })
        return
      }

      const matches = nodes
        .filter(node => searchNodeContent(node, term))
        .map(node => node.id)

      setSearchMatches(matches)
      setCurrentMatchIndex(0)

      // Update node data with search match information
      nodes.forEach(node => {
        const isMatch = matches.includes(node.id)
        const isCurrentMatch = matches.length > 0 && matches[0] === node.id

        // Only update if the data has changed
        if (node.data?.isSearchMatch !== isMatch || node.data?.isCurrentSearchMatch !== isCurrentMatch) {
          setNodeSynced({
            ...node,
            data: {
              ...node.data,
              isSearchMatch: isMatch,
              isCurrentSearchMatch: isCurrentMatch,
            }
          })
        }
      })
    }, 700),
    [nodes, searchNodeContent, setNodeSynced]
  )

  // Handle search input change
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value)
    debouncedSearch(value)
  }, [debouncedSearch])

  // Navigate to next match
  const handleNextMatch = useCallback(() => {
    if (searchMatches.length > 0) {
      const newIndex = (currentMatchIndex + 1) % searchMatches.length
      setCurrentMatchIndex(newIndex)

      // Update current match highlighting
      searchMatches.forEach((nodeId, index) => {
        const node = nodes.find(n => n.id === nodeId)
        if (node) {
          setNodeSynced({
            ...node,
            data: {
              ...node.data,
              isSearchMatch: true,
              isCurrentSearchMatch: index === newIndex,
            }
          })
        }
      })
    }
  }, [searchMatches, currentMatchIndex, nodes, setNodeSynced])

  // Navigate to current match
  const handleGoToMatch = useCallback(() => {
    if (searchMatches.length > 0 && searchMatches[currentMatchIndex]) {
      const nodeId = searchMatches[currentMatchIndex]
      const node = getNode(nodeId)
      if (node) {
        const nodePosition = node.position
        const nodeWidth = node.measured?.width || 0
        setCenter(nodePosition.x + nodeWidth / 2, nodePosition.y + 400, {
          zoom: 1,
          duration: 430,
        })
        setNodeSynced({
          ...node,
          data: {
            ...node.data,
            isSearchMatch: false,
            isCurrentSearchMatch: null,
          }
        })
      }
      setIsSearchVisible(false)

    }
  }, [searchMatches, currentMatchIndex, getNode, setCenter])

  // Handle search keyboard shortcuts
  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleGoToMatch()
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      handleNextMatch()
      // Also zoom to the next match but keep search box open
      setTimeout(() => {
        if (searchMatches.length > 0) {
          const nextIndex = (currentMatchIndex + 1) % searchMatches.length
          const nodeId = searchMatches[nextIndex]
          const node = getNode(nodeId)
          if (node) {
            const nodePosition = node.position
            const nodeWidth = node.measured?.width || 0
            setCenter(nodePosition.x + nodeWidth / 2, nodePosition.y + 400, {
              zoom: 1,
              duration: 430,
            })
          }
        }
      }, 100) // Small delay to ensure state is updated
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setIsSearchVisible(false)
      setSearchTerm('')
      setSearchMatches([])
    }
  }, [handleGoToMatch, handleNextMatch, searchMatches, currentMatchIndex, getNode, setCenter])

  // Show search input
  const handleShowSearch = useCallback(() => {
    setIsSearchVisible(true)
    setTimeout(() => {
      searchInputRef.current?.focus()
    }, 100)
  }, [])

  // Enhanced keyboard shortcuts
  const { handleKeyDown } = useKeyboardShortcuts(
    globalNodeEdgeUndoManager,
    nodes,
    copyNodeToClipboard,
    pasteNodeFromClipboard,
    showNodeIndex,
    showFileDialog,
    nodeMenu,
    paneMenu,
    sectionMenu,
    handleShowSearch, // Add search handler
    () => {
      setShowGlitch(true)
      // Auto-hide glitch after 1500ms to match initialization
      setTimeout(() => setShowGlitch(false), 1500)
    } // Add glitch trigger
  )  // Use file handlers hook
  const { handleFileDrop, onDragOver } = useFlowFileHandlers({
    projectId: projectId!,
    user,
    screenToFlowPosition,
    addNode,
    createNodeResource,
  })  // Use data handlers hook  
  const { visibleNodes, deleteNodeWithGroups } = useFlowData({
    nodes,
    edges: validEdges,
    removeNode,
    onNodesChange,
    setPanOnScroll,
  })

  // Use global handlers hook
  useFlowGlobalHandlers({
    nodes,
    user,
    screenToFlowPosition,
    getNode,
    updateNodeData,
    addNode,
    handleSectionSelect,
    deleteNodeWithGroups, // Use the proper deleteNodeWithGroups function
    duplicateNode,
  })// Use project settings hook
  const {
    projectSettings,
    projectSettingsAccessRules,
    projectInfo: projectDataFromHook,
    fetchProjectSettings,
  } = useFlowProjectSettings({
    projectId: projectId!,
    showProjectSettings
  })

  // Add autolayout functionality
  const {
    applyAutoLayout,
    applyHorizontalLayout,
    applyVerticalLayout,
    applyGridLayout,
    applyOptimizedGridLayout, } = useAutoLayout()

  useEffect(() => {
    if (ref.current) setFlowBounds(ref.current.getBoundingClientRect())
  }, [])




  const edgeHandlers = useEdgeHandler()

  const onDrop = (event: React.DragEvent) => {
    // Only handle ReactFlow node type drops, not file drops
    if (event.dataTransfer.types.includes('application/reactflow')) {
      event.preventDefault()
      const type = event.dataTransfer.getData('application/reactflow')
      const position = screenToFlowPosition({
        x: event.clientX - 80,
        y: event.clientY - 20,
      })
      const newNode = {
        id: `${Date.now()}`,
        type,
        position,
        data: { label: `${type}`, informationClassification: 'Data' },
      }
      addNode(newNode)
    }
    // If it's a file drop, do nothing here (handleFileDrop will handle it)
  }

  const handleNodesChange = useCallback(
    (props) => {
      if (props[0]?.type === 'select' && props[0]?.selected)
        setPanOnScroll(true)
      else if (props[0]?.type === 'select' && !props[0]?.selected)
        setPanOnScroll(false)
      onNodesChange(props)
    },
    [setPanOnScroll, onNodesChange]
  )

  const handleNodeSelect = useCallback(
    (nodeId: string) => {
      const node = getNode(nodeId)
      if (node) {
        const nodePosition = node.position
        const nodeWidth = node.measured?.width || 0
        setCenter(nodePosition.x + nodeWidth / 2, nodePosition.y + 400, {
          zoom: 1,
          duration: 430,
        })
      }

      setShowNodeIndex(false)
    },
    [getNode, setCenter]
  )


  const createAndAddNode = useCallback(
    (nodeType: string) => {
      createNodeByType(nodeType)
      setPaneMenu(null)
    },
    [createNodeByType, setPaneMenu]
  )

  const onPaneContextMenuWithPaste = useCallback(
    async (event) => {
      onPaneContextMenu(event)
      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      })
      let hasClipboardNode = hasCopiedNode
      try {
        const clipboardText = await navigator.clipboard.readText()
        const clipboardData = JSON.parse(clipboardText)
        hasClipboardNode = !!clipboardData['spechops-node-data']
      } catch (e) {
        hasClipboardNode = false
      }
      setPaneMenu((prev) =>
        prev
          ? {
            ...prev,
            hasCopiedNode,
            onPaste: hasClipboardNode
              ? async () => {
                await pasteNodeFromClipboard({ x: position.x, y: position.y })
                setPaneMenu(null)
              }
              : undefined,
          }
          : prev
      )
    },
    [
      onPaneContextMenu,
      screenToFlowPosition,
      hasCopiedNode,
      pasteNodeFromClipboard,
      setPaneMenu,
    ]
  )

  const onNodeContextMenuWithCopy = useCallback(
    (event, node) => {
      onNodeContextMenu(event, node)
      setNodeMenu((prev) =>
        prev
          ? {
            ...prev,
            onCopy: () => {
              copyNodeToClipboard(node.id)
              setNodeMenu(null)
            },
          }
          : prev
      )
    },
    [onNodeContextMenu, copyNodeToClipboard, setNodeMenu]
  )


  useEffect(() => {
    if (globalNodeEdgeUndoManager) {
      window.addEventListener('keydown', handleKeyDown)
      return () => window.removeEventListener('keydown', handleKeyDown)
    }
  }, [globalNodeEdgeUndoManager, handleKeyDown])
  const handlePaneOnClickAndCleanupActiveUsers = useCallback(
    () => {
      if (!user.email) return

      const currentNodeId = localStorage.getItem('currentNodeId')
      const lastNodeId = localStorage.getItem('lastNodeId')      // If no nodes are selected, clear user presence

      // Clear localStorage
      localStorage.removeItem('currentNodeId')
      localStorage.removeItem('lastNodeId')

      // Update nodes to remove user presence
      setNodes((nds) =>
        nds.map((node) => {
          if (
            (node.id === currentNodeId || node.id === lastNodeId) &&
            node.data?.activeUsers
          ) {
            const updatedNode = { ...node, data: { ...node.data } }
            if (updatedNode.data.activeUsers && typeof updatedNode.data.activeUsers === 'object') {
              delete (updatedNode.data.activeUsers as Record<string, any>)[user.email]
            }
            return updatedNode
          }
          return node
        })
      )

      // Update presence node
      const presenceNode = findPresenceNode()
      if (presenceNode && presenceNode.data?.activeUsers?.[user.email]) {
        const updatedPresenceNode = {
          ...presenceNode,
          data: {
            ...presenceNode.data,
            activeUsers: { ...presenceNode.data.activeUsers },
          },
        }
        updatedPresenceNode.data.activeUsers[user.email] = {
          ...updatedPresenceNode.data.activeUsers[user.email],
          currentNodeId: null,
          lastSeen: Date.now(),
          status: 'online',
        }
        setNodeSynced(updatedPresenceNode)
      }
      // If a single node is selected, update user presence

      // Update nodes
      setNodes((nds) =>
        nds.map((node) => {
          if (
            (node.id === currentNodeId || node.id === lastNodeId) &&
            node.data?.activeUsers
          ) {
            const updatedNode = {
              ...node,
              data: { ...node.data, activeUsers: { ...node.data.activeUsers } },
            }
            delete updatedNode.data.activeUsers[user.email]
            return updatedNode
          }
          return node
        })
      )
    },
    [user.email, setNodes, setNodeSynced, findPresenceNode]
  )

  // Fetch project settings and access rules when opening modal  // --- Add this effect for global click handling ---
  useEffect(() => {
    function handleGlobalClick(event: MouseEvent) {
      // If click is inside any context menu, do nothing
      const contextMenu = (event.target as HTMLElement).closest(
        '.contextMenu, .paneContextMenu, .sectionContextMenu'
      )
      if (contextMenu) return

      // Otherwise, close all menus
      if (nodeMenu) setNodeMenu(null)
      if (paneMenu) setPaneMenu(null)
      if (sectionMenu?.isVisible) setSectionMenu({ isVisible: false })
    }

    document.addEventListener('click', handleGlobalClick) // <--- remove 'true' for bubbling phase
    return () => document.removeEventListener('click', handleGlobalClick)
  }, [
    nodeMenu,
    paneMenu,
    sectionMenu,
    setNodeMenu,
    setPaneMenu,
    setSectionMenu,])  // --- end global click handler ---    // Set up event listener for creating NodeWebExtract nodes
  useEffect(() => {
    const handleCreateWebExtractNode = (event: CustomEvent) => {
      const { nodeId, url, extractionType, mode } = event.detail

      const position = screenToFlowPosition({
        x: window.innerWidth / 2 + Math.random() * 100,
        y: window.innerHeight / 2 + Math.random() * 100,
      })

      const newNode = {
        id: nodeId || `web-extract-${Date.now()}`,
        type: 'nodewebextract',
        position,
        width: 900,
        height: 630,
        data: {
          label: `Web Extract - ${new URL(url).hostname}`,
          url: url,
          extractionType: extractionType || 'webpage',
          mode: mode || 'normal',
          informationClassification: 'Information',
          createdBy: user.email,
          createdAt: new Date().toISOString(),
          version: 1,
        },
        draggable: true,
        selectable: true,
        connectable: true,
      }

      addNode(newNode)
    }

    document.addEventListener('create-web-extract-node', handleCreateWebExtractNode)
    return () => document.removeEventListener('create-web-extract-node', handleCreateWebExtractNode)
  }, [screenToFlowPosition, addNode, user.email])

  // Set up event listener for creating NodeResource nodes with URL resource type
  useEffect(() => {
    const handleCreateResourceUrlNode = (event: CustomEvent) => {
      const {
        nodeId,
        filename,
        url,
        contentType,
        isUrlResource,
        urlToDownload,
        downloadStatus,
        downloadAttempted
      } = event.detail

      const position = screenToFlowPosition({
        x: window.innerWidth / 2 + Math.random() * 100,
        y: window.innerHeight / 2 + Math.random() * 100,
      })

      const newNode = {
        id: nodeId || `resource-url-${Date.now()}`,
        type: 'noderesource',
        position,
        width: 400,
        height: 300,
        data: {
          label: filename || 'Downloaded Resource',
          fileId: nodeId,
          filename: filename || 'downloaded-file',
          url: url,
          contentType: contentType || 'application/octet-stream',
          size: 0, // Unknown until downloaded
          createdAt: new Date().toISOString(),
          groupId: null,
          // URL resource type fields
          isUrlResource: isUrlResource || false,
          urlToDownload: urlToDownload || url,
          downloadStatus: downloadStatus || 'pending',
          downloadAttempted: downloadAttempted || false,
          informationClassification: 'Information',
          createdBy: user.email,
          version: 1,
        },
        draggable: true,
        selectable: true,
        connectable: true,
      }

      addNode(newNode)
    }

    document.addEventListener('create-resource-url-node', handleCreateResourceUrlNode)
    return () => document.removeEventListener('create-resource-url-node', handleCreateResourceUrlNode)
  }, [screenToFlowPosition, addNode, user.email])

  // Set up event listener for creating Google Drive Resource nodes
  useEffect(() => {
    const handleCreateGoogleDriveResourceNode = (event: CustomEvent) => {
      const {
        nodeId,
        googleDriveUrl,
        filename,
        isGoogleDriveFile
      } = event.detail

      const position = screenToFlowPosition({
        x: window.innerWidth / 2 + Math.random() * 100,
        y: window.innerHeight / 2 + Math.random() * 100,
      })

      const newNode = {
        id: nodeId || `google-drive-resource-${Date.now()}`,
        type: 'noderesource',
        position,
        width: 400,
        height: 300,
        data: {
          label: filename || 'Google Drive File',
          fileId: '', // Will be set after download
          filename: filename || 'Google Drive File',
          url: '', // Will be set after upload to blob storage
          contentType: 'application/octet-stream', // Will be updated after processing
          size: 0, // Will be updated after processing
          createdAt: new Date().toISOString(),
          groupId: null,
          // Google Drive specific fields
          isGoogleDriveFile: isGoogleDriveFile || true,
          googleDriveUrl: googleDriveUrl,
          googleDriveProcessing: false,
          informationClassification: 'Information',
          createdBy: user.email,
          version: 1,
          // Set up the command to trigger Google Drive processing
          documentRequest: {
            'sysagent-cmd': 'NodeGoogleDrive-Upload-Started',
            'sysagent-response': '',
            processing: false
          }
        },
        draggable: true,
        selectable: true,
        connectable: true,
      }

      addNode(newNode)

      // Show success message
      toast.success(`Google Drive resource node created. Processing will begin automatically.`)
    }

    document.addEventListener('create-google-drive-resource-node', handleCreateGoogleDriveResourceNode)
    return () => document.removeEventListener('create-google-drive-resource-node', handleCreateGoogleDriveResourceNode)
  }, [screenToFlowPosition, addNode, user.email])

  // Set up event listener for creating basic NodeResource nodes from FileResourcingDialog
  useEffect(() => {
    const handleCreateResourceNode = (event: CustomEvent) => {
      const { fileId, filename, url, contentType, size, createdAt, groupId } = event.detail
      
      const position = screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      })

      createNodeResource({
        fileId,
        filename,
        url,
        contentType,
        size,
        createdAt,
        groupId,
        position
      })

      // Show success message
      toast.success(`Resource node created for ${filename}`)
    }

    document.addEventListener('create-resource-node', handleCreateResourceNode)
    return () => document.removeEventListener('create-resource-node', handleCreateResourceNode)
  }, [screenToFlowPosition, createNodeResource])

  // Set up event listener for creating PDF resource nodes from FileResourcingDialog
  useEffect(() => {
    const handleCreateResourcePdfNode = (event: CustomEvent) => {
      const { fileId, filename, url, contentType, size, createdAt, groupId } = event.detail
      
      const position = screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      })

      createNodeResource({
        fileId,
        filename,
        url,
        contentType,
        size,
        createdAt,
        groupId,
        position
      })

      // Show success message
      toast.success(`PDF resource node created for ${filename}`)
    }

    document.addEventListener('create-resource-pdf-node', handleCreateResourcePdfNode)
    return () => document.removeEventListener('create-resource-pdf-node', handleCreateResourcePdfNode)
  }, [screenToFlowPosition, createNodeResource])

  // Handler for starting the initialization tour
  const handleStartInitializationTour = useCallback(() => {
    let targetNode;

    if (startingAngle === 'quickstart') {
      targetNode = nodes.find(node => node.type === 'nodequickstart');
    } else if (startingAngle === 'quickstartdynamic') {
      targetNode = nodes.find(node => node.type === 'nodequickstartdynamic');
    } else {
      // For other starting angles, try to find a matching node
      targetNode = nodes.find(node => node.type === `node${startingAngle}`);
    }

    if (targetNode) {
      // Set the nodeId ref FIRST and let it stabilize
      initializationTourNodeIdRef.current = targetNode.id;

      // Small delay to ensure the ref is set before the tour starts
      setTimeout(() => {
        setShowInitializationTour(true);
        setIntroEnabled(true);
      }, 10);
    }
  }, [nodes, startingAngle]);

  return (
    <>
      <Toaster position="top-right" />
      {/* Project Initialization Message */}
      {showInitializationMessage && startingAngle && (
        <InitializationMessage
          message={initializationMessage}
          steps={initializationSteps}
          currentStep={currentStep}
          onDismiss={dismissInitializationMessage}
          onNextStep={nextStep}
          onPreviousStep={previousStep}
          startingAngle={startingAngle}
          onStartGuidedTour={handleStartInitializationTour}
        />
      )}

      <div className={`flowContainer ${showGlitch ? 'with-glitch' : ''}`}>
        {/* Render overlays using modular component */}
        <FlowOverlays
          projectId={projectId!}
          projectName={projectName || 'Untitled Project'}
          projectDescription={projectInfo?.projectDescription || ''}
          userName={user.name || user.email}
          theme={theme}
          toggleTheme={toggleTheme}
          backgroundPreset={backgroundPreset}
          backgroundPresets={backgroundPresets}
          setBackgroundPreset={setBackgroundPreset}
          cursors={cursors}
          zoomLevel={zoomLevel}
          showMap={showMap}
          hasCopiedNode={hasCopiedNode}
          onPasteNode={() => pasteNodeFromClipboard()}
          onShowFileDialog={() => setShowFileDialog(true)}
          onShowHelpGuide={() => setIntroEnabled(true)}
          onShowNodeIndex={() => setShowNodeIndex(true)}
          onShowProjectSettings={() => setShowProjectSettings(true)}
          onShowBlueprints={() => setShowBlueprints(true)}          // Selected node props for SidebarPresence
          selectedNodeId={sidePanelState.selectedNodeId || undefined}
          selectedNodeData={sidePanelState.selectedNodeId ? getNode(sidePanelState.selectedNodeId)?.data : undefined}
          // Search props
          isSearchVisible={isSearchVisible}
          searchTerm={searchTerm}
          searchMatches={searchMatches}
          currentMatchIndex={currentMatchIndex}
          searchInputRef={searchInputRef}
          onSearchChange={handleSearchChange}
          onSearchKeyDown={handleSearchKeyDown}
          onSearchClose={() => {
            setIsSearchVisible(false)
            setSearchTerm('')
            setSearchMatches([])
          }}
        />
        {/* Render dialogs using modular component */}
        <FlowDialogs
          projectId={projectId!}
          projectName={projectName || 'Untitled Project'}
          projectDescription={projectInfo?.projectDescription || ''}
          projectType={projectDataFromHook?.projectType}
          organizationName={projectDataFromHook?.organizationName}
          userId={user.id}
          showProjectSettings={showProjectSettings}
          setShowProjectSettings={setShowProjectSettings}
          showBlueprints={showBlueprints}
          setShowBlueprints={setShowBlueprints}
          showFileDialog={showFileDialog}
          setShowFileDialog={setShowFileDialog}
          showNodeIndex={showNodeIndex}
          setShowNodeIndex={setShowNodeIndex}
          nodes={nodes}
          onNodeSelect={handleNodeSelect}
          projectSettings={projectSettings}
          projectSettingsAccessRules={projectSettingsAccessRules}
          fetchProjectSettings={fetchProjectSettings}
        />

        {/* Render main flow canvas using modular component */}
        <FlowCanvas
          ref={ref}
          projectName={projectName || undefined}
          nodes={nodes}
          edges={edges}
          visibleNodes={visibleNodes}
          validEdges={validEdges}
          user={user}
          viewportSettings={viewportSettings}
          setViewportSettings={setViewportSettings}
          panOnScroll={panOnScroll}
          setPanOnScroll={setPanOnScroll}
          handleNodesChange={handleNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeDragStart={onNodeDragStart}
          onNodeDragStop={onNodeDragStop}
          onNodeClick={onNodeClick}
          selectNode={selectNode}
          edgeHandlers={edgeHandlers}
          onMove={onMove}
          onPaneClick={onPaneClick}
          handlePaneOnClickAndCleanupActiveUsers={handlePaneOnClickAndCleanupActiveUsers}
          onNodeContextMenuWithCopy={onNodeContextMenuWithCopy}
          onPaneContextMenuWithPaste={onPaneContextMenuWithPaste}
          onMouseMove={onMouseMove}
          theme={theme}
          currentBackgroundPreset={currentBackgroundPreset}
          zoomLevel={zoomLevel}
          showMap={showMap}
          setShowNodeIndex={setShowNodeIndex}
          handleNodeSelect={handleNodeSelect}
          cursors={cursors}
          setDebug={setDebug}
          newNode={newNode}
          setShowMap={setShowMap}
          onCreatingAnnotationNode={onCreatingAnnotationNode}
          createCustomNode={() => { }} // TODO: implement or remove
          createQuickStartNode={createQuickStartNode}
          onCreatingRandomSequenceNode={onCreatingRandomSequenceNode}
          createDocumentNode={createDocumentNode}
          createChatNode={() => { }} // TODO: implement or remove
          createCompareFilesNode={() => { }} // TODO: implement or remove
          createSmartNotesNode={createSmartNotesNode}
          createDocumentDesignerNode={createDocumentDesignerNode}
          createNodeIntellica={createNodeIntellica}
          createNodeDiagrams={createNodeDiagrams}
          createResearcherNode={() => { }} // TODO: implement or remove
          createFileMinerNode={createFileMinerNode}
          createJavascriptNode={createJavascriptNode}
          createImageGeneratorNode={createImageGeneratorNode}
          createNodeResource={() => {
            // This is called by FlowControls which doesn't pass params
            // For file drops, use handleFileDrop instead
          }}
          applyAutoLayout={applyAutoLayout}
          applyHorizontalLayout={applyHorizontalLayout}
          applyVerticalLayout={applyVerticalLayout}
          applyGridLayout={applyGridLayout}
          applyOptimizedGridLayout={applyOptimizedGridLayout}
          handleFileDrop={handleFileDrop}
          onDrop={onDrop}
          onDragOver={onDragOver}
          setFlowBounds={setFlowBounds}
          initializeViewport={initializeViewport}
        />

        {/* Render context menus using modular component */}
        <FlowContextMenus
          nodeMenu={nodeMenu}
          paneMenu={paneMenu}
          sectionMenu={sectionMenu}
          nodes={nodes}
          duplicateNode={duplicateNode}
          removeNode={removeNode}
          setNodeMenu={setNodeMenu}
          setPaneMenu={setPaneMenu}
          setSectionMenu={setSectionMenu}
          createAndAddNode={createAndAddNode}
          handleSectionStateChange={handleSectionStateChange}
          updateNodeData={updateNodeData}
          getNode={getNode}
          createDocumentNodeWithContent={createDocumentNodeWithContent}
        />
        {/* Intro guide steps - show main flow tour OR initialization tour */}
        {(!showInitializationMessage || showInitializationTour) && (
          <Steps
            enabled={introEnabled}
            steps={currentSteps}
            initialStep={showInitializationTour ? 0 : introStepsIndex} onExit={() => {
              if (showInitializationTour) {
                setShowInitializationTour(false);
                initializationTourNodeIdRef.current = undefined;
              }
              setIntroEnabled(false);
            }}
            onBeforeChange={(nextStepIndex) => {
              if (showInitializationTour) {
                // Handle initialization tour step changes
              } else {
                setIntroStepsIndex(nextStepIndex)
                return false
              }
            }} onComplete={() => {
              if (showInitializationTour) {
                setShowInitializationTour(false);
                initializationTourNodeIdRef.current = undefined;
              }
              setIntroEnabled(false);
            }}
            options={currentOptions}
          />
        )}

        {/* Bottom bar overlay is already included in FlowOverlays */}
      </div>
    </>
  )
}

export default function Index() {
  return (
    <ReactFlowProvider>
      <ErrorBoundary FallbackComponent={ErrorFallback} onReset={() => { }}>
        <Flow />
      </ErrorBoundary>
    </ReactFlowProvider>
  )
}
